/**
 * REST routes served by Kit19API_V2 (E:\Tfs2022\NewAPI2022\Kit19API_V2).
 *
 * Each route is called as POST {API_BASE}/{route} with the standard
 * { Token, Details } wrapper — except `waSendTemplate`, which the API accepts
 * as a raw JObject exactly as the legacy page posted it.
 *
 * Nothing here points at a .asmx / WCF service; every legacy WebMethod or WCF
 * call the Kit19 entity screens made is mapped to its REST equivalent below
 * (routes marked NEW were added to UserCRMController.cs as part of this work).
 */

export const ROUTES = {
  /* ------------------------------------------------------------ entities */
  /** Recent leads, used by the launcher to offer real records. */
  leadList: 'UserCRM/GetLeadList',
  /** Returns the record, custom fields, deal stages and mask rows together. */
  leadDetail: 'UserCRM/GetLeadDetailByLeadIdNew',
  enquiryDetail: 'UserCRM/GetEnquiryDetailByIdNew',
  scoreCard: 'UserCRM/funcToGetScoreCardDetails',

  /* ------------------------------------------------------------- widgets */
  /** The activity feed (`LeadActivity_List`). */
  activities: 'UserCRM/GetLeadActivity',
  notes: 'UserCRM/GetNotes',
  documents: 'UserCRM/GetleadUploadDocument',
  deleteDocument: 'UserCRM/DeleteDocument',
  /** NEW (re-enabled) — logs the document deletion against the lead's activity feed. */
  deleteDocActivity: 'UserCRM/DeleteDocLeadActivity',
  /** Resolves a stored document to a viewable URL. */
  documentLink: 'UserCRM/disp_hyperlink',
  /** Outcome list used by the activity filter. */
  outcomes: 'UserCRM/GetAllOutcomeByUserId',
  /** NEW — tag autocomplete for the edit screen's tag box. */
  tagList: 'UserCRM/GetTagListWithFilter',
  tasksByLead: 'UserCRM/GetTaskListByLeadId',
  callLogList: 'UserCRM/GetCallLogListById',
  /** NEW — call logs for one lead/enquiry, filtered server-side. */
  callLogListByEntity: 'UserCRM/GetCallLogListByEntity',
  tawkToChatLogs: 'UserCRM/GetTawkToChatLogsByLeadId',
  webforms: 'UserCRM/GetWebformDetailsByLeadId',

  /* --------------------------------------------- secondary entity detail */
  taskDetail: 'UserCRM/GetTaskDetailById',
  callLogDetail: 'UserCRM/GetCallLogDetailById',
  /** Same SP the legacy ToDoService.asmx/GetPhysicalAppointmentById ran. */
  physicalAppointment: 'UserCRM/GetPhysicalAppointmentById',
  /** From ToDoService.asmx/GetIntractionHistories */
  interactionHistories: 'UserCRM/GetIntractionHistories',
  userFromHashedList: 'UserCRM/GetUserFromHashedList',

  /* ------------------------------------------------------------ comments */
  taskComments: 'UserCRM/GetTaskCommentsById',
  saveTaskComment: 'UserCRM/SaveTaskComment',

  /* -------------------------------------------------------- edit screens */
  editLead: 'UserCRM/EditLead',
  editEnquiry: 'UserCRM/EditEnquiry',
  /** From CRMService.asmx/EntityEditGetCustomFields */
  entityEditCustomFields: 'UserCRM/EntityEditGetCustomFields',
  /** From CRMService.asmx/GetDependentFieldData */
  dependentFieldData: 'UserCRM/GetDependentFieldData',
  /** From CRMService.asmx/SaveInlineEdit */
  saveInlineEdit: 'UserCRM/SaveInlineEdit',
  /** NEW — from CRMService.asmx/GetInitalRemarksOnly (enquiry Add Notes). */
  initialRemarks: 'UserCRM/GetInitalRemarksOnly',
  /** NEW — from CRMService.asmx/UpdateIntialRemarks. */
  updateInitialRemarks: 'UserCRM/UpdateIntialRemarks',

  /* ----------------------------------------------------------- lookups */
  countries: 'Common/GetCountryList',
  states: 'Common/GetStateListByCountryId',
  cities: 'Common/GetCityListByStateId',
  sources: 'UserCRM/GetSourceSettingsList',
  mediums: 'Common/GetUserMediumSettingsByParentId',
  campaigns: 'Common/GetUserCampaignSettingsByParentId',

  /* ------------------------------------------------- WhatsApp / security */
  convUserId: 'UserCRM/GetConvUserID',
  waConversation: 'IMSMOB/GetConversationAsRequiredConvId',
  /** InvoiceService.asmx/IsLeadRegisterForWhatsapp — has the entity a WA profile? */
  waRegistration: 'UserCRM/IsLeadRegisterForWhatsapp',
  /**
   * IMSService.asmx/GetChannelTemplateListByUserId — approved WA templates.
   * The `_ByParam` variant takes the user id in Details; the plain
   * GetChannelTemplateList reads it from the caller's token, which the shared
   * universal token does not carry, so that one always answers "Error".
   */
  waChannelTemplates: 'UserCRM/GetChannelTemplateListByUserId_ByParam',
  /** IMSService.asmx/GetActiveApiKey — key sent with the WA template payload. */
  waActiveApiKey: 'IMSMOB/GetActiveApiKey',
  /** jsWaEntityMangager — candidate numbers to bind to the entity. */
  waAssociatedMobileNos: 'IMSMOB/GetAssociatedMobileNo',
  waAssociate: 'IMSMOB/AssociateDeissociateWrapper',
  /** Raw-JObject route: same payload the legacy page posted to IMS/Whatsapp/Template. */
  waSendTemplate: 'IMS/Whatsapp/Template',
  permissions: 'UserAuth/GetAllPermissionByModuleCodeUserId',
  fieldMasked: 'Common/GetFieldMasked',

  /* ------------------------------------------------------------ uploads */
  uploadAttachment: 'Common/UploadAttachment',

  /* ------------------------------------------------------ action widgets */
  /** CRMService.asmx/AddLeadFollowup_DLT — the followup save the edit page used. */
  addFollowup: 'UserCRM/AddLeadFollowup_DLT',
  followupStatusList: 'UserCRM/GetFollowUpStatus_new',
  usersList: 'UserCRM/GetUserParentIDWise',
  productsList: 'UserCRM/GetProductListByUserId',
  /** LeadDetails.aspx/GetProductsByName — the followup form's product typeahead. */
  productsByName: 'UserCRM/GetProductsByName',
  /** NEW — IMSService.asmx/GetDltTemplateStatus (reminder-SMS gating). */
  dltTemplateStatus: 'UserCRM/GetDltTemplateStatus',
  /** CRMService.asmx/SendSms — the send the edit page's SMS action used. */
  sendSms: 'UserCRM/SendSms',
  /** IMSService.asmx/getSmsSenderIds → proc_displaySenderNameOfGSMType_DLT. */
  smsSenderIds: 'UserCRM/GetSmsSenderID',
  /** IMSService.asmx/getSmsTemplates → Usp_GetDLTTemplateBySenderName. */
  smsTemplates: 'UserCRM/GetSmsTemplate',
  /** CRMService.asmx/Get_DLTTemplateText — template body + unicode flag. */
  dltTemplateDetails: 'UserCRM/GetDLTTemplateDetails',
  /** CRMService.asmx/ValidateDLTTemplateText — pre-send compliance check. */
  validateDltTemplate: 'UserCRM/ValidateDLTTemplateText',
  /** TriggerCampaign.asmx/FuncToGetEntityPlaceHolder — placeholder tokens. */
  entityPlaceholders: 'UserCRM/FuncToGetEntityPlaceHolder',
  /** CRMService.asmx/SendEmail. */
  sendEmail: 'UserCRM/SendEmail',
  /** IMSService.asmx/getEmailTemplateList → usp_GetUserMailTemplateBind. */
  emailTemplates: 'UserCRM/GetEnquiryMailTemplate',
  /** CRMService.asmx/funcToGetFromMailID — From / Reply-To dropdowns. */
  fromMailIds: 'UserCRM/GetFromMailId',
  /** TriggerCampaign.asmx/FuncToGetTemplateSubject — subject autofill. */
  mailTemplateSubject: 'UserCRM/GetMailTemplateSubject',
  /** CRMService.asmx/SendVoiceEnquiry — voice campaign send. */
  sendVoice: 'UserCRM/SendVoiceEnquiryOrLead',
  /** IMSService.asmx/GetDIDNo. */
  didNumbers: 'UserCRM/GetDIDNumbers',
  /** IMSService.asmx/getAppName — IVR app flows. */
  appNames: 'UserCRM/GetAppNames',
  /** CRMService.asmx/AddNotes → usp_InsertUpdateLeadNotes. */
  addNote: 'UserCRM/AddNotes',
  /** Multipart (details JSON + `file` part) — replacement for LeadHandler.ashx ImsLeadDocument. */
  uploadDoc: 'UserCRM/UploadLeadDocument',
  /** Appointment.aspx/InsertNewTaskOrAppointment — tasks AND appointments. */
  addTaskOrAppointment: 'UserCRM/InsertNewTaskOrAppointment',
  /** ToDoService.asmx/GetPredefinedOutcomeListByUserId. */
  predefinedOutcomes: 'UserCRM/GetPredefinedOutcomeListByUserId',
  /** ToDoService.asmx/GetOutcomeListByTaskId. */
  outcomesByTaskType: 'UserCRM/GetOutcomeListByTaskId',
  /** Task types for the Add Task form. */
  taskTypes: 'UserCRM/GetSalesActivityTaskTypeListByParentId',
  /** IMSService.asmx/SaveDealData → deal save the edit page used. */
  saveDeal: 'UserCRM/SaveDealData',
  /** IMSService.asmx/getDealPipeline → Usp_GetPipeLines. Details = ParentId (raw number). */
  dealPipelines: 'UserCRM/GetDealPipeline',
  /** IMSService.asmx/getDealPipelineStage. Details = PipelineId (raw number). */
  dealPipelineStages: 'UserCRM/GetDealPipelineStage',
  /** IMSService.asmx/getDealPipelineStageReason. */
  dealPipelineStageReasons: 'UserCRM/GetDealPipelineStageReason',
  /** CRMService.asmx/GetUserWebformListByUserId → ups_wf_GetWebFormByUserId. */
  userWebforms: 'UserCRM/GetUserWebformListByUserId',
} as const
